// ============================================================================
// CHANGO V5.0 — BOM Items Library (Task #119 / Round 12)
// ============================================================================
// Manage BOM items (物料库) — raw materials, semi-products, finished goods.
// Mount: /app/factory/bom-items
//
// Features:
//   - List all un-archived items with category + unit + cost (boss/finance see cost)
//   - Filter by category dropdown + search keyword
//   - Create new item (modal)
//   - Inline detail link → BomItemDetail.jsx
//   - Soft archive if no BOM references
//   - boss/finance only
// ============================================================================

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, HttpError } from '../../api/client.js';
import ExcelImportModal from '../../components/ExcelImportModal.jsx';
import { useAuth } from '../../hooks/useAuth.js';

export default function BomItemsLibrary() {
  const { user } = useAuth() || {};
  const isSales = user?.role === 'sales';

  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Filters
  const [categoryFilter, setCategoryFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Modal form
  const [form, setForm] = useState({
    code: '',
    name: '',
    name_en: '',
    category_id: '',
    unit: '',
    cost_rmb: '',
    supplier: '',
    notes: ''
  });

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await api.get('/api/material-categories');
        setCategories(res?.items || []);
      } catch (err) {
        // Ignore if not found
      }
    };
    fetchCategories();
  }, []);

  const loadItems = async () => {
    try {
      setLoading(true);
      setError(null);
      const qs = new URLSearchParams();
      if (categoryFilter) qs.set('category_id', categoryFilter);
      if (searchQuery) qs.set('q', searchQuery);
      const res = await api.get(`/api/bom-items?${qs.toString()}`);
      setItems(res?.items || []);
    } catch (err) {
      if (!(err instanceof HttpError && err.status === 404)) {
        setError(err.message || '物料加载失败');
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch items
  useEffect(() => {
    loadItems();
  }, [categoryFilter, searchQuery]);

  // Check sales role
  if (isSales) {
    return (
      <div className="px-6 py-12 max-w-4xl mx-auto">
        <div className="rounded-md border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-800">
          权限不足：仅老板和财务可访问工厂管理页面
        </div>
      </div>
    );
  }

  const handleCreateClick = () => {
    setForm({
      code: '',
      name: '',
      name_en: '',
      category_id: '',
      unit: '',
      cost_rmb: '',
      supplier: '',
      notes: ''
    });
    setEditingId(null);
    setShowModal(true);
  };

  const handleEditClick = (item) => {
    setForm({
      code: item.code,
      name: item.name,
      name_en: item.name_en || '',
      category_id: item.category_id || '',
      unit: item.unit || '',
      cost_rmb: item.cost_rmb ?? '',
      supplier: item.supplier || '',
      notes: item.notes || ''
    });
    setEditingId(item.id);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setForm({
      code: '',
      name: '',
      name_en: '',
      category_id: '',
      unit: '',
      cost_rmb: '',
      supplier: '',
      notes: ''
    });
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.name.trim()) {
      setError('请填写物料名称');
      return;
    }

    if (!editingId && !form.code.trim()) {
      setError('请填写物料编码');
      return;
    }

    try {
      setError(null);
      const payload = {
        name: form.name.trim(),
        name_en: form.name_en.trim() || null,
        category_id: form.category_id ? Number(form.category_id) : null,
        unit: form.unit.trim() || null,
        cost_rmb: form.cost_rmb ? Number(form.cost_rmb) : null,
        supplier: form.supplier.trim() || null,
        notes: form.notes.trim() || null
      };

      if (editingId) {
        await api.patch(`/api/bom-items/${editingId}`, payload);
      } else {
        await api.post('/api/bom-items', {
          code: form.code.trim(),
          ...payload
        });
      }

      await loadItems();
      handleModalClose();
    } catch (err) {
      setError(err.message || '物料保存失败');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('确认归档这个物料？已被 BOM 引用的物料不能归档。')) {
      return;
    }

    try {
      await api.delete(`/api/bom-items/${id}`);
      await loadItems();
    } catch (err) {
      setError(err.message || '物料归档失败');
    }
  };

  return (
    <div className="px-6 py-6 max-w-7xl mx-auto">
      <header className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-800">物料库</h1>
          <p className="text-sm text-slate-500">管理原料、零件和产品</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowImportModal(true)}
            className="btn-ghost"
          >
            📥 批量导入
          </button>
          <button
            onClick={handleCreateClick}
            className="btn-primary"
          >
            + 新增物料
          </button>
        </div>
      </header>

      {error && (
        <div className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-800">
          {error}
        </div>
      )}

      <div className="card mb-4 p-4 flex gap-3">
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="input-text"
        >
          <option value="">全部分类</option>
          {categories.map((cat) => (
            <option key={cat.id} value={cat.id}>
              {cat.name}
            </option>
          ))}
        </select>

        <input
          type="search"
          placeholder="搜索物料编码 / 名称..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="input-text flex-1"
        />
      </div>

      <div className="card overflow-hidden">
        {loading && (
          <div className="px-4 py-8 text-center text-slate-400">正在加载...</div>
        )}

        {!loading && items.length === 0 && (
          <div className="px-4 py-8 text-center text-slate-400">
            暂无物料 · <button onClick={handleCreateClick} className="text-chango-700 hover:underline">新增物料</button>
          </div>
        )}

        {!loading && items.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">物料编码</th>
                <th className="px-4 py-3">物料名称</th>
                <th className="px-4 py-3">分类</th>
                <th className="px-4 py-3">单位</th>
                {!isSales && <th className="px-4 py-3 text-right">单位成本</th>}
                <th className="px-4 py-3 text-right">操作</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const cat = categories.find((c) => c.id === item.category_id);
                return (
                  <tr key={item.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono text-xs">{item.code}</td>
                    <td className="px-4 py-3">{item.name}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{cat?.name || '—'}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{item.unit || '—'}</td>
                    {!isSales && (
                      <td className="px-4 py-3 text-right text-sm">
                        {item.cost_rmb != null ? `¥${Number(item.cost_rmb).toFixed(2)}` : '—'}
                      </td>
                    )}
                    <td className="px-4 py-3 text-right">
                      <Link
                        to={`/app/factory/bom-items/${encodeURIComponent(item.code)}`}
                        className="text-xs text-chango-700 hover:underline mr-3"
                      >
                        详情
                      </Link>
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="text-xs text-rose-600 hover:underline"
                      >
                        归档
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="mt-4">
        <Link to="/app/dashboard" className="text-sm text-slate-600 hover:underline">
          返回工作台
        </Link>
      </div>

      {showImportModal && (
        <ExcelImportModal
          entity="bomItems"
          onComplete={() => {
            setShowImportModal(false);
            loadItems();
          }}
          onClose={() => setShowImportModal(false)}
        />
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full mx-4 max-h-96 overflow-y-auto">
            <h2 className="text-lg font-semibold mb-4">
              {editingId ? '编辑物料' : '新增物料'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-3">
              {!editingId && (
                <label className="text-xs block">
                  物料编码 *
                  <input
                    type="text"
                    className="field mt-1"
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value })}
                    placeholder="例如：碳布-T700、bolt-m8"
                    maxLength="80"
                  />
                </label>
              )}

              <label className="text-xs block">
                物料名称 *
                <input
                  type="text"
                  className="field mt-1"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="例如：东丽碳布 T700"
                  maxLength="200"
                />
              </label>

              <label className="text-xs block">
                英文名称
                <input
                  type="text"
                  className="field mt-1"
                  value={form.name_en}
                  onChange={(e) => setForm({ ...form, name_en: e.target.value })}
                  placeholder="例如：Carbon Cloth"
                  maxLength="200"
                />
              </label>

              <label className="text-xs block">
                物料分类
                <select
                  className="field mt-1"
                  value={form.category_id}
                  onChange={(e) => setForm({ ...form, category_id: e.target.value })}
                >
                  <option value="">— 请选择 —</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="text-xs block">
                单位
                <input
                  type="text"
                  className="field mt-1"
                  value={form.unit}
                  onChange={(e) => setForm({ ...form, unit: e.target.value })}
                  placeholder="例如：件、kg、m"
                  maxLength="20"
                />
              </label>

              <label className="text-xs block">
                单位成本（元）
                <input
                  type="number"
                  className="field mt-1"
                  value={form.cost_rmb}
                  onChange={(e) => setForm({ ...form, cost_rmb: e.target.value })}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                />
              </label>

              <label className="text-xs block">
                供应商
                <input
                  type="text"
                  className="field mt-1"
                  value={form.supplier}
                  onChange={(e) => setForm({ ...form, supplier: e.target.value })}
                  placeholder="例如：供应商 A"
                  maxLength="100"
                />
              </label>

              <label className="text-xs block">
                备注
                <textarea
                  className="field mt-1"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="内部备注..."
                  rows="2"
                  maxLength="500"
                />
              </label>

              <div className="flex justify-end gap-2 pt-4">
                <button
                  type="button"
                  onClick={handleModalClose}
                  className="btn-ghost text-sm"
                >
                  取消
                </button>
                <button
                  type="submit"
                  className="btn-primary text-sm"
                >
                  {editingId ? '保存' : '创建'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
